<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSearchableDropdown } from 'dashboard/composables/useSearchableDropdown';

const props = defineProps({
  hiddenColumns: { type: Array, required: true }, // colunas com visible: false
});

const emit = defineEmits(['add']);

const { t } = useI18n();
const isOpen = ref(false);
const triggerEl = ref(null);
const dropdownPos = ref({ top: 0, left: 0 });

const columnLabel = col => (col.isCustom ? col.label : t(col.labelKey));

function filterColumns(query) {
  const q = query.toLowerCase();
  if (!q) return props.hiddenColumns;
  return props.hiddenColumns
    .filter(c => columnLabel(c).toLowerCase().includes(q))
    .sort(
      (a, b) =>
        columnLabel(a).toLowerCase().indexOf(q) -
        columnLabel(b).toLowerCase().indexOf(q)
    );
}

function selectColumn(col) {
  emit('add', col.key);
  isOpen.value = false;
}

const { search, highlightedIndex, handleKeydown, resetHighlight } =
  useSearchableDropdown({
    items: computed(() => filterColumns(search.value)),
    onSelect: selectColumn,
    onClose: () => {
      isOpen.value = false;
    },
  });

const filteredColumns = computed(() => filterColumns(search.value));

function toggleOpen() {
  isOpen.value = !isOpen.value;
  search.value = '';
  resetHighlight();
  if (isOpen.value && triggerEl.value) {
    const rect = triggerEl.value.getBoundingClientRect();
    dropdownPos.value = { top: rect.bottom + 4, left: rect.left };
  }
}

function onClickOutside(e) {
  if (
    !e.target.closest('[data-add-column-menu]') &&
    !e.target.closest('[data-add-column-dropdown]')
  ) {
    isOpen.value = false;
  }
}

function onWindowKeydown(e) {
  if (isOpen.value) handleKeydown(e);
}

onMounted(() => {
  document.addEventListener('mousedown', onClickOutside);
  window.addEventListener('keydown', onWindowKeydown);
});
onUnmounted(() => {
  document.removeEventListener('mousedown', onClickOutside);
  window.removeEventListener('keydown', onWindowKeydown);
});
</script>

<template>
  <div data-add-column-menu class="relative flex-shrink-0">
    <button
      ref="triggerEl"
      class="flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors focus:outline-none"
      :title="t('KANBAN.LIST_VIEW.ADD_COLUMN')"
      @click.stop="toggleOpen"
    >
      <span class="i-lucide-plus size-4" />
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        data-add-column-dropdown
        class="fixed bg-n-background border border-n-weak rounded-xl shadow-xl py-1 w-56 z-50"
        :style="{ top: dropdownPos.top + 'px', left: dropdownPos.left + 'px' }"
        @click.stop
      >
        <div class="px-3 py-1.5 border-b border-n-weak">
          <span v-if="search" class="text-sm text-n-slate-10">{{
            search
          }}</span>
          <span v-else class="text-sm text-n-slate-10">{{
            t('KANBAN.LIST_VIEW.ADD_COLUMN_SEARCH_PLACEHOLDER')
          }}</span>
        </div>

        <button
          v-for="(col, index) in filteredColumns"
          :key="col.key"
          class="w-full text-left px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors text-n-slate-12"
          :class="{ 'bg-n-alpha-1': index === highlightedIndex }"
          @click="selectColumn(col)"
        >
          {{ columnLabel(col) }}
        </button>

        <p
          v-if="!filteredColumns.length"
          class="px-3 py-2 text-sm text-n-slate-10 italic"
        >
          {{ t('KANBAN.LIST_VIEW.NO_CUSTOM_FIELDS') }}
        </p>
      </div>
    </Teleport>
  </div>
</template>
